import React, { useState, useEffect, useMemo } from 'react';
import { simulateDay } from '../services/api.ts';
import { useHospitals, useOperatingRooms } from '../data/queries.ts';
import { getBoardWorkingDate, getCentralOffsetStringForDate } from '../utils/timezone.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import { NAMEPLATE_COLORS } from './staffNameplates.ts';
import { saveFloatSlots, clampFloatSlots, MAX_FLOAT_CRNA_SLOTS } from './floatCrnas.ts';
import { clearAllDayOverlays } from './boardViewHelpers.ts';

// Every simulated case must START before this local hour so morning/pecking-order staff
// are on the floor to be allocated (afternoon-only cases don't test anything useful).
const MORNING_START_CUTOFF_LOCAL_HOUR = 9.5; // 9:30am

// Shift options per role. Colors mirror the board's shift legend (staffNameplates.ts).
// - open-in (doctors/students): available from 5:45am, arrival derived from earliest room.
// - window (CRNAs): fixed start -> end (2230-0700 crosses midnight).
// - pecking (doctors/CRNAs): full-day availability + priority rank.
const C = NAMEPLATE_COLORS;
type ShiftSpec = {
  role: 'doctor' | 'crna' | 'student';
  mode: 'open-in' | 'window' | 'pecking';
  outLocalHour?: number;
  startLocalHour?: number;
  endLocalHour?: number;
};
type ShiftOption = { key: string; label: string; color: string; spec: ShiftSpec };

const DOCTOR_SHIFTS: ShiftOption[] = [
  { key: 'doc-out15', label: 'Any time in — out by 3:00 PM', color: C.docOut3pm, spec: { role: 'doctor', mode: 'open-in', outLocalHour: 15 } },
  { key: 'doc-out17', label: 'Any time in — out by 5:00 PM', color: C.docOut5pm, spec: { role: 'doctor', mode: 'open-in', outLocalHour: 17 } },
  { key: 'doc-out19', label: 'Any time in — out by 7:00 PM', color: C.docOut7pm, spec: { role: 'doctor', mode: 'open-in', outLocalHour: 19 } },
  { key: 'doc-pecking', label: 'Pecking order', color: C.docSupervise, spec: { role: 'doctor', mode: 'pecking' } },
];
const CRNA_SHIFTS: ShiftOption[] = [
  { key: 'crna-0530-1400', label: '0530 – 1400', color: C.crnaOut3pm,    spec: { role: 'crna', mode: 'window', startLocalHour: 5.5,  endLocalHour: 14 } },
  { key: 'crna-0630-1500', label: '0630 – 1500', color: C.crnaOut3pm,    spec: { role: 'crna', mode: 'window', startLocalHour: 6.5,  endLocalHour: 15 } },
  { key: 'crna-0630-1700', label: '0630 – 1700', color: C.crnaOut5pm,    spec: { role: 'crna', mode: 'window', startLocalHour: 6.5,  endLocalHour: 17 } },
  { key: 'crna-0530-1900', label: '0530 – 1900', color: C.crnaOut7pm,    spec: { role: 'crna', mode: 'window', startLocalHour: 5.5,  endLocalHour: 19 } },
  { key: 'crna-0630-1900', label: '0630 – 1900', color: C.crnaOut7pm,    spec: { role: 'crna', mode: 'window', startLocalHour: 6.5,  endLocalHour: 19 } },
  { key: 'crna-1430-2300', label: '1430 – 2300', color: C.crnaOut7pm,    spec: { role: 'crna', mode: 'window', startLocalHour: 14.5, endLocalHour: 23 } },
  { key: 'crna-2230-0700', label: '2230 – 0700', color: C.crnaOvernight, spec: { role: 'crna', mode: 'window', startLocalHour: 22.5, endLocalHour: 7 } },
  { key: 'crna-pecking',   label: 'Pecking order', color: C.crnaDefault, spec: { role: 'crna', mode: 'pecking' } },
];
const STUDENT_SHIFTS: ShiftOption[] = [
  { key: 'stu-out15', label: 'Open in — out 3:00 PM', color: C.studentGreen, spec: { role: 'student', mode: 'open-in', outLocalHour: 15 } },
  { key: 'stu-out17', label: 'Open in — out 5:00 PM', color: C.studentGreen, spec: { role: 'student', mode: 'open-in', outLocalHour: 17 } },
];
const ALL_SHIFT_OPTIONS = [...DOCTOR_SHIFTS, ...CRNA_SHIFTS, ...STUDENT_SHIFTS];

const SimulateForm: React.FC = () => {
  const { isAdmin } = useAuth();
  // Use the SAME working date the board is viewing (not just today), so a new sim generates for — and clears
  // the stale per-day overlays (float CRNAs, room transfers, slot marks) for — the exact date the board loads.
  const [date, setDate] = useState<string>(getBoardWorkingDate);
  const [staffCounts, setStaffCounts] = useState<Record<string, number>>({});
  // Dedicated specialty rooms — one room per specialist (1:1). 0 => none generated.
  const [cardiacDocs, setCardiacDocs] = useState<number>(0);
  const [obDocs, setObDocs] = useState<number>(0);
  // Pediatric anesthesiologists on the roster — required to supervise at children's hospitals.
  const [pediatricDocs, setPediatricDocs] = useState<number>(0);
  // Pediatric roster as its OWN world: pecking-order pediatric docs (own #1..N) + fixed-shift pediatric docs.
  // They staff children's hospitals first; spares compete for adult rooms.
  const [pediatricPeckingDocs, setPediatricPeckingDocs] = useState<number>(0);
  const [pediatricShiftDocs1500, setPediatricShiftDocs1500] = useState<number>(0);
  const [pediatricShiftDocs1700, setPediatricShiftDocs1700] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Sites (hospitals) + how many ORs each has, so we can list them and cap the per-site
  // room count at the site's real OR count.
  // From the shared cache (data/queries.ts); derived here rather than copied into state.
  const hospitalsQuery = useHospitals();
  const roomsQuery = useOperatingRooms();
  const hospitals = useMemo(
    () => (hospitalsQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)) as { hospitalId: string; name: string }[],
    [hospitalsQuery.data],
  );
  const orCountByHospital = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const or of (roomsQuery.data ?? [])) counts[or.hospitalId] = (counts[or.hospitalId] || 0) + 1;
    return counts;
  }, [roomsQuery.data]);
  const [siteSel, setSiteSel] = useState<Record<string, { checked: boolean; count: number }>>({});
  // Per-site FLOAT CRNA slot count — extra CRNAs assigned to the whole site (not a room). The Board
  // draws this many empty "Float CRNA" boxes under the site's doctor coins. Typically only big sites.
  const [floatCrnaBySite, setFloatCrnaBySite] = useState<Record<string, number>>({});

  const referenceFailed = !!(hospitalsQuery.error || roomsQuery.error);
  useEffect(() => {
    if (referenceFailed) setMessage({ type: 'error', text: 'Could not load sites and operating rooms.' });
  }, [referenceFailed]);

  const setStaffCount = (key: string, raw: number) =>
    setStaffCounts(prev => ({ ...prev, [key]: Math.max(0, Math.min(999, Math.floor(raw) || 0)) }));

  const toggleSite = (id: string) => setSiteSel(prev => {
    const cur = prev[id] || { checked: false, count: 0 };
    const willCheck = !cur.checked;
    const orCount = orCountByHospital[id] || 0;
    return { ...prev, [id]: { checked: willCheck, count: willCheck && cur.count === 0 ? orCount : cur.count } };
  });
  const setSiteCount = (id: string, raw: number) => setSiteSel(prev => {
    const orCount = orCountByHospital[id] || 0;
    const count = Math.max(0, Math.min(orCount, Math.floor(raw) || 0));
    return { ...prev, [id]: { checked: (prev[id]?.checked ?? false) || count > 0, count } };
  });
  const setSiteFloatCrnas = (id: string, raw: number) =>
    setFloatCrnaBySite(prev => ({ ...prev, [id]: clampFloatSlots(raw) }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Per-site room selection (checked sites with a positive count).
    const siteOrCounts: Record<string, number> = {};
    for (const h of hospitals) {
      const sel = siteSel[h.hospitalId];
      if (sel?.checked && sel.count > 0) siteOrCounts[h.hospitalId] = Math.min(sel.count, orCountByHospital[h.hospitalId] || 0);
    }
    if (Object.keys(siteOrCounts).length === 0) {
      setMessage({ type: 'error', text: 'Check at least one site and set how many of its rooms to open.' });
      return;
    }

    // Shift roster (options with a positive count).
    const shiftSpecs = ALL_SHIFT_OPTIONS
      .filter(o => (staffCounts[o.key] || 0) > 0)
      .map(o => ({ ...o.spec, count: staffCounts[o.key] }));

    setLoading(true);
    try {
      // Float CRNA slot counts for the sites actually being run (0 for a run site with none, so a
      // re-run clears a stale count). Sites not in this run are left untouched by the backend.
      const floatCrnaCountBySite: Record<string, number> = {};
      for (const hid of Object.keys(siteOrCounts)) floatCrnaCountBySite[hid] = floatCrnaBySite[hid] || 0;

      const response = await simulateDay({
        date,
        siteOrCounts,
        floatCrnaCountBySite,
        shiftSpecs,
        numCardiacDocs: cardiacDocs,
        numPediatricPeckingDocs: pediatricPeckingDocs,
        numPediatricShiftDocs1500: pediatricShiftDocs1500,
        numPediatricShiftDocs1700: pediatricShiftDocs1700,
        numObDocs: obDocs,
        numPediatricDocs: pediatricDocs,
        // Generate the day in the SAME timezone the board's Allocate uses (DST-aware US Central), so the
        // 9:30am in-scope cap and shift/case times agree end-to-end. Without this the backend defaulted to UTC
        // while Allocate ran in Central, pushing borderline-morning rooms past the cutoff -> deferred/unstaffed.
        timezone: getCentralOffsetStringForDate(date),
        // Replace the day's prior simulated data instead of stacking runs on top.
        replaceDay: true,
        latestStartLocalHour: MORNING_START_CUTOFF_LOCAL_HOUR,
        // Morning-surge starts: 85% of room openings hit the 7:00-7:30 "seven o'clock start",
        // ~5% run 6:30-7:00 (the earliest rooms), ~10% trickle in 7:30-9:30. See
        // generateMorningSurgeStart in backend/src/simulate.ts.
        distributionStrategy: 'morning-surge'
      });
      // A new simulation replaces the WHOLE day, so wipe EVERY per-day board overlay for this date — room &
      // nurse transfers, relief, floats, site locks, dispositions/in-times, case markers, NEED/NONE slot marks,
      // and the move log/undo. Anything left behind is keyed to the OLD staff/rooms and mis-renders against the
      // fresh data (e.g. a stale room transfer hides a doctor's early rooms, so the board shows them "missing"
      // and coming in hours late). A clean day means a clean day — no partial carry-over.
      clearAllDayOverlays(date);
      // Record THIS day's float-slot counts in a per-day overlay (oc-float-slots-<date>). Floats are now
      // per-day: the board and the allocator read THIS map for THIS date, so a float set here never bleeds
      // onto another day. Wholesale-replaces the day's config (0 = no entry), so re-simulating with no floats
      // clears them. Replaces the old global Hospital.floatCrnaSlots field.
      saveFloatSlots(date, floatCrnaCountBySite);
      setMessage({
        type: 'success',
        text: `Simulation completed successfully! Created ${response.data?.shifts || 0} shifts and ${response.data?.schedules || 0} schedules.`
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to run simulation' });
    } finally {
      setLoading(false);
    }
  };

  // One shift-option row: board color swatch + label + a click/scrollable count box.
  // Plain render helper (not a nested component) so the input keeps focus while typing.
  const optionRow = (o: ShiftOption) => (
    <div key={o.key} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '4px 2px', borderBottom: '1px solid var(--border-subtle)' }}>
      <span title="board shift color" style={{ width: '11px', height: '11px', borderRadius: '2px', background: o.color, flexShrink: 0, border: '1px solid rgba(0,0,0,0.2)' }} />
      <label htmlFor={o.key} style={{ flex: 1, minWidth: 0, margin: 0, fontSize: '0.8em' }}>{o.label}</label>
      <input
        type="number"
        id={o.key}
        value={staffCounts[o.key] || ''}
        placeholder="0"
        onChange={e => setStaffCount(o.key, parseInt(e.target.value))}
        min="0"
        max="999"
        className="form-control"
        style={{ width: '58px', textAlign: 'center', flexShrink: 0, padding: '2px 4px', fontSize: '0.82em' }}
      />
    </div>
  );
  const column = (title: string, subtitle: string, options: ShiftOption[]) => (
    <div style={{ flex: '1 1 210px', minWidth: '200px' }}>
      <h3 style={{ margin: '0 0 2px' }}>{title}</h3>
      <small className="form-text" style={{ display: 'block', marginBottom: '6px', minHeight: '2.4em' }}>{subtitle}</small>
      {options.map(optionRow)}
    </div>
  );

  if (!isAdmin) {
    return (
      <div className="card">
        <h1>Simulate Cases &amp; Shifts</h1>
        <ReadOnlyBanner message="Simulation is restricted to administrators." />
      </div>
    );
  }

  return (
    <div className="card">
      <h1>Simulate Cases &amp; Shifts</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>
        Generate mock data for testing the allocation algorithm. Pick your staff by role and shift, and
        which sites/rooms to run. Each opened room gets a case <strong>before 9:30&nbsp;am</strong> local
        (then fills through the day), so morning and pecking-order staff are on the floor to be allocated.
      </p>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="date">Date</label>
          <input type="date" id="date" name="date" value={date} onChange={e => setDate(e.target.value)} required className="form-control" style={{ maxWidth: '220px' }} />
        </div>

        {/* Staff by role — colors match the board's shift legend */}
        <h3 style={{ margin: '10px 0 2px' }}>Staff</h3>
        <small className="form-text" style={{ display: 'block', marginBottom: '8px' }}>
          Counts per shift; the swatch is the same color that shift shows on the board.
        </small>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
          {column('Doctors', 'Open-in — arrive 30 min before their earliest room.', DOCTOR_SHIFTS)}
          {column('CRNAs', 'Fixed shift windows.', CRNA_SHIFTS)}
          {column('Students', 'Open-in — all treated as senior (green).', STUDENT_SHIFTS)}
        </div>

        {/* Specialty rooms — cardiac + OB, one dedicated room per specialist */}
        <div style={{ marginTop: '22px' }}>
          <h3 style={{ margin: '0 0 2px' }}>Specialty rooms</h3>
          <small className="form-text" style={{ display: 'block', marginBottom: '8px' }}>
            One dedicated room per specialist. <strong>Cardiac</strong> rooms get only heart cases
            (staffed by a cardiac anesthesiologist); <strong>OB</strong> rooms get only cesarean
            cases (any doctor covers them). All other rooms get neither. Leave at 0 to generate no
            cardiac/cesarean cases. These rooms are carved from the rooms you open below (and placed
            at hospital sites first).
          </small>
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px' }}>
              <label htmlFor="cardiacDocs" style={{ margin: 0, fontSize: '0.85em' }}>Cardiac anesthesiologists</label>
              <input
                type="number" id="cardiacDocs" min="0" max="999" placeholder="0"
                value={cardiacDocs || ''}
                onChange={e => setCardiacDocs(Math.max(0, Math.min(999, Math.floor(parseInt(e.target.value)) || 0)))}
                className="form-control"
                style={{ width: '64px', textAlign: 'center', padding: '2px 4px', fontSize: '0.82em' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px' }}>
              <label htmlFor="obDocs" style={{ margin: 0, fontSize: '0.85em' }}>OB anesthesiologists</label>
              <input
                type="number" id="obDocs" min="0" max="999" placeholder="0"
                value={obDocs || ''}
                onChange={e => setObDocs(Math.max(0, Math.min(999, Math.floor(parseInt(e.target.value)) || 0)))}
                className="form-control"
                style={{ width: '64px', textAlign: 'center', padding: '2px 4px', fontSize: '0.82em' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px' }}>
              <label htmlFor="pediatricDocs" style={{ margin: 0, fontSize: '0.85em' }}>Pediatric anesthesiologists</label>
              <input
                type="number" id="pediatricDocs" min="0" max="999" placeholder="0"
                value={pediatricDocs || ''}
                onChange={e => setPediatricDocs(Math.max(0, Math.min(999, Math.floor(parseInt(e.target.value)) || 0)))}
                className="form-control"
                style={{ width: '64px', textAlign: 'center', padding: '2px 4px', fontSize: '0.82em' }}
              />
            </div>
            {/* Pediatric roster as its OWN world: pecking order + fixed-shift docs. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px' }}>
              <label style={{ margin: 0, fontSize: '0.85em' }}>Pediatric pecking‑order docs</label>
              <input type="number" min="0" max="999" placeholder="0" value={pediatricPeckingDocs || ''}
                onChange={e => setPediatricPeckingDocs(Math.max(0, Math.min(999, Math.floor(parseInt(e.target.value)) || 0)))}
                className="form-control" style={{ width: '64px', textAlign: 'center', padding: '2px 4px', fontSize: '0.82em' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px' }}>
              <label style={{ margin: 0, fontSize: '0.85em' }}>Pediatric shift docs (out 3pm)</label>
              <input type="number" min="0" max="999" placeholder="0" value={pediatricShiftDocs1500 || ''}
                onChange={e => setPediatricShiftDocs1500(Math.max(0, Math.min(999, Math.floor(parseInt(e.target.value)) || 0)))}
                className="form-control" style={{ width: '64px', textAlign: 'center', padding: '2px 4px', fontSize: '0.82em' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 2px' }}>
              <label style={{ margin: 0, fontSize: '0.85em' }}>Pediatric shift docs (out 5pm)</label>
              <input type="number" min="0" max="999" placeholder="0" value={pediatricShiftDocs1700 || ''}
                onChange={e => setPediatricShiftDocs1700(Math.max(0, Math.min(999, Math.floor(parseInt(e.target.value)) || 0)))}
                className="form-control" style={{ width: '64px', textAlign: 'center', padding: '2px 4px', fontSize: '0.82em' }} />
            </div>
          </div>
          <small className="form-text" style={{ display: 'block', marginTop: '4px' }}>
            <strong>Pediatric anesthesiologists</strong> are their own staffing world — a <strong>pecking order</strong> and
            <strong> shift docs</strong> of their own. They staff <strong>children's hospital</strong> rooms first (by the pediatric
            pecking ramp); spares then compete for adult hospital / surgery‑center rooms. Add enough to cover the children's
            rooms you open below. (The single "Pediatric anesthesiologists" box above is the legacy all‑day option.)
          </small>
        </div>

        {/* OR cases by site */}
        <div style={{ marginTop: '22px', marginBottom: '2px' }}>
          <h3 style={{ margin: '0 0 2px' }}>OR cases by site</h3>
          <small className="form-text">
            Check the sites to run, then set how many of each site's rooms open (capped at what it has).
            Each opened room gets a case before 9:30&nbsp;am, then fills through the day to a spread of
            out times (3/5/7/9&nbsp;pm) so late rooms get tested. <strong>Float CRNAs</strong> are extra
            CRNAs for the whole site (typically only the bigger sites) — they show on the board as empty
            boxes under the doctor coins that you can pre-assign.
          </small>
        </div>
        {hospitals.length === 0 ? (
          <small className="form-text">Loading sites…</small>
        ) : (
          hospitals.map(h => {
            const orCount = orCountByHospital[h.hospitalId] || 0;
            const sel = siteSel[h.hospitalId] || { checked: false, count: 0 };
            return (
              <div key={h.hospitalId} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 2px', borderBottom: '1px solid var(--border-subtle)', opacity: orCount === 0 ? 0.5 : 1 }}>
                <input type="checkbox" checked={sel.checked} disabled={orCount === 0} onChange={() => toggleSite(h.hospitalId)} style={{ flexShrink: 0, width: '16px', height: '16px' }} />
                <label style={{ margin: 0, fontSize: '0.9em', flex: 1, minWidth: 0 }}>{h.name}</label>
                <input type="number" value={sel.count || ''} placeholder="0" onChange={e => setSiteCount(h.hospitalId, parseInt(e.target.value))} min="0" max={orCount} disabled={!sel.checked || orCount === 0} className="form-control" style={{ width: '72px', textAlign: 'center', flexShrink: 0 }} />
                <span style={{ fontSize: '0.75em', color: 'var(--text-muted)', width: '54px', flexShrink: 0 }}>of {orCount}</span>
                <span style={{ fontSize: '0.72em', color: 'var(--text-muted)', flexShrink: 0 }} title="Float CRNAs: extra CRNAs for the whole site (empty boxes on the board, under the doctor coins)">float CRNAs</span>
                <input type="number" value={floatCrnaBySite[h.hospitalId] || ''} placeholder="0" onChange={e => setSiteFloatCrnas(h.hospitalId, parseInt(e.target.value))} min="0" max={MAX_FLOAT_CRNA_SLOTS} disabled={!sel.checked || orCount === 0} className="form-control" title="Float CRNAs at this site" style={{ width: '58px', textAlign: 'center', flexShrink: 0 }} />
              </div>
            );
          })
        )}

        <button type="submit" disabled={loading} className="btn btn-primary" style={{ marginTop: '18px' }}>
          {loading ? 'Running Simulation...' : 'Run Simulation'}
        </button>
      </form>

      <div className="card" style={{ marginTop: '30px', backgroundColor: 'var(--bg-surface-raised)' }}>
        <h3>What This Does</h3>
        <ul style={{ marginBottom: 0 }}>
          <li><strong>Doctors &amp; students</strong> are "open-in": available early, arrival set 30&nbsp;min before their earliest room, out at their fixed time.</li>
          <li><strong>CRNAs</strong> work fixed shift windows (including a 2230–0700 overnight).</li>
          <li><strong>Pecking-order</strong> doctors and CRNAs are ranked and stay until released.</li>
          <li>For each site you check, opens that many rooms — a case <strong>before 9:30&nbsp;am</strong>, then filled to a spread of out times (3/5/7/9&nbsp;pm) so late rooms draw a late shift worker or a bottom-of-pecking-order person.</li>
          <li>Each run <strong>replaces</strong> that day's simulated shifts and cases — it does not stack on top of an earlier run for the same day.</li>
        </ul>
        <p style={{ marginTop: '15px', marginBottom: 0, color: 'var(--warning-text)', backgroundColor: 'var(--warning-soft)', padding: '10px', borderRadius: '4px', border: '1px solid rgba(217, 119, 6, 0.4)' }}>
          <strong>Note:</strong> Make sure you have enough operating rooms and employees of each type to cover the counts you pick.
        </p>
      </div>
    </div>
  );
};

export default SimulateForm;
